import styled from '@emotion/styled'
import type { FC, PropsWithChildren } from 'react'
import sanitizeHtml from 'sanitize-html'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Typography from '@segment/matcha/components/Typography'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

const copy = {
  heading: `Methodology`,
  description: [
    `Twilio Segment conducted this research using an online survey prepared by <a href="https://www.methodcommunications.com/methodresearch/">Method Research</a> and distributed by <a href="https://www.cint.com/">Cint</a> among n=1,300 full-time employees working in marketing and customer services roles at B2B companies with 75+ employees.`,
    `The sample consisted of n=1,000 respondents from the United States and n=300 from the United Kingdom. Data was collected from August 3 to August 17, 2022.`,
  ],
  disclaimer: ` This report, The Growth Report, contains statistical data, estimates and expectations that are based on the above publicly available report. This data does not represent, and should not be construed as indicators of Twilio's historical or future financial performance or the actual or potential demand for Twilio's products and solutions, and such information is solely presented as context for broader market trends.`,
}

const Introduction: FC<PropsWithChildren<unknown>> = () => {
  return (
    <SectionBase variant="noCopy">
      <Section.Contents>
        <CopyTextBase>
          <StyledOverline variant="overline" tag="h2">
            {copy.heading}
          </StyledOverline>

          <Content>
            <ColumnTrack
              overrides={{
                lg: 2,
              }}
            >
              <List>
                {copy.description.map((text) => (
                  <Li key={text}>
                    <Text
                      variant="pMedium"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(text, { allowedTags: [`a`] }),
                      }}
                    />
                  </Li>
                ))}
              </List>

              <DisclaimerWrapper tag="p" variant="pMedium">
                <DisclaimerTitle variant="pMedium" tag="span">
                  Disclaimer:
                </DisclaimerTitle>
                {copy.disclaimer}
              </DisclaimerWrapper>
            </ColumnTrack>
          </Content>
        </CopyTextBase>
      </Section.Contents>
    </SectionBase>
  )
}

const Content = styled.div`
  margin-top: 40px;
`

const StyledOverline = styled(CopyText.Overline)`
  color: ${({ theme }) => theme.palette.brand.segmentGreen[500]};
`

const DisclaimerWrapper = styled(CopyText.Description)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-left: 20px;
  }

  margin: 0;
`

const DisclaimerTitle = styled(Typography)`
  font-weight: 600;
  font-style: italic;
  color: ${({ theme }) => theme.palette.brand.darkAcai[500]};
`

const List = styled.ul`
  padding-left: 24px;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-right: 30px;
  }
`

const Li = styled.li`
  :not(:first-of-type) {
    margin: 28px auto;
  }

  :last-of-type {
    margin-bottom: 0;
  }
`

const Text = styled(Typography)`
  margin-top: 0;
`
export default Introduction
